import React, { useCallback, useState } from "react";
import { Alert, Box, Button, CircularProgress, Dialog, Link, Typography } from "@mui/material";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { func } from "prop-types";
import legalContent from "./legalContent";
import ROUTES from "../router/routesModel";
import { acceptTerms } from "../users/service/userApi";
import useHandleUsers from "../users/hooks/useHandleUsers";
import { useDismissible } from "../native/dismissibleStack";
import { claimRefusals } from "../utils/refusalNotice";

export const POLICY_UPDATE_TITLE = "We've updated our Terms and Privacy Policy";

/**
 * What changed in THIS update (2026-10-06), in plain words. Rewrite it for the
 * next one: it describes the difference from the previous version, not the
 * whole policy.
 *
 * It matches the policy as published, which differs from the wording first
 * approved in one place: the shop address is not shown to other collectors
 * (getUser, discovery and search all leave it out), so this does not say it
 * is public.
 *
 * "This version does not" (Mapbox performance data) is true of every build
 * that can show this screen: the screen and performanceMetricsCollection={false}
 * in MapView shipped together.
 */
export const POLICY_UPDATE_CHANGES = [
  "If your phone can't find where you are in time, which often happens indoors, we may estimate your rough area from your internet (IP) address, using a service called ipapi.co. We use it only to centre your map, and never if you have said no to location.",
  "We now say what Mapbox, our map provider, receives: your IP address, the part of the map you are looking at (which shows roughly where you are, because the map opens there), the places you search for (and, when you search for a spot for Locked mode, your area to about 1 kilometre), and a random ID stored on your device. Older versions of the app also send Mapbox data about how well the map runs and about your device; this version does not.",
  "You can add a phone number to your profile if you want to, and shop accounts can add the shop's address. Only you and our administrators can see them.",
  "Precise mode also moves your pin, by roughly 25 to 150 metres, so it never shows exactly where you are.",
  "The app collects your location in every privacy mode, including Locked and Hidden, while location permission is on. Your privacy mode only changes what other people see. To stop the collection, turn off location permission for Tradeloft in your phone's settings. To also take your position off the map, switch to Hidden mode. To have the position we stored deleted, ask us, or delete your account.",
  "The Terms of Service have not changed apart from their date.",
];

/**
 * Pages the screen steps aside for: the two documents it asks about, and the
 * deletion page it offers as a way out. Every other route shows it again, so
 * this is somewhere to read, not a way into the app.
 */
const OPEN_ROUTES = [ROUTES.PRIVACY, ROUTES.TERMS, ROUTES.DELETE_ACCOUNT_REQUEST].map((route) =>
  route.toLowerCase()
);

/**
 * The path as React Router matches it: a trailing slash and letter case make
 * no difference to which page renders, so "/privacy/" and "/Privacy" are the
 * Privacy Policy and must not be covered either. The whole path is compared,
 * never a prefix.
 */
const isOpenRoute = (pathname) => {
  const normalised = (pathname || "").replace(/\/+$/, "").toLowerCase();
  return OPEN_ROUTES.includes(normalised);
};

const ignoreBack = () => {};

/**
 * Asks an account that accepted an older Terms and Privacy Policy to accept the
 * current one (owner decision Q-20; the policy's CHANGES section promises it).
 *
 * Rendered by FirstRunConsentGate, which decides between the two consent
 * screens from ONE consent check, so a brand-new account never sees this one
 * straight after the first-run screen.
 *
 * One tap: there is no checkbox. The first-run screen's box carries the 18+
 * statement, which an existing account has already made, and the Play
 * reviewer's demo account has to get through here with a single press.
 *
 * Not dismissible, like the first-run screen: Escape is disabled and Android
 * Back is claimed and swallowed. The ways out are the button, Sign out, and the
 * links to the documents and to the deletion page, which it steps aside for.
 */
const PolicyUpdateGate = ({ onAccepted }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { handleLogout } = useHandleUsers();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const visible = !isOpenRoute(pathname);
  useDismissible(visible, ignoreBack, "policy-update-gate");

  // Focus the title whenever the screen appears, so a screen reader starts at
  // the heading. A ref callback runs as the element mounts, before MUI's focus
  // trap looks for focus, so the trap keeps it there instead of taking it to
  // the dialog box.
  const focusTitle = useCallback((node) => {
    if (node) node.focus();
  }, []);

  const accept = async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      await acceptTerms(legalContent.version);
      onAccepted();
    } catch (e) {
      // Shown on this screen (B-31).
      claimRefusals();
      setError(typeof e === "string" ? e : e?.message || "Could not record your acceptance. Please try again.");
      setSaving(false);
    }
  };

  const signOut = () => {
    Promise.resolve(handleLogout()).catch(() => {});
    navigate(ROUTES.LOGIN, { replace: true });
  };

  if (!visible) return null;

  return (
    <Dialog
      open
      fullScreen
      disableEscapeKeyDown
      aria-labelledby="policy-update-title"
      data-testid="policy-update-gate"
      PaperProps={{
        sx: {
          // A 16 px gutter at 320 px wide; the gesture bar and status bar are
          // cleared because API 36 enforces edge-to-edge.
          px: { xs: 2, sm: 3 },
          pb: "calc(24px + var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))",
          pt: "calc(24px + var(--safe-area-inset-top, env(safe-area-inset-top, 0px)))",
          overflowY: "auto",
          overflowWrap: "anywhere",
        },
      }}
    >
      {/* Centred with auto margins rather than justify-content, which would cut
          off the top of the text on a short screen instead of letting it scroll. */}
      <Box sx={{ my: "auto", mx: "auto", width: "100%", maxWidth: 520, display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography
          id="policy-update-title"
          ref={focusTitle}
          tabIndex={-1}
          variant="h5"
          component="h1"
          sx={{ fontWeight: 900, outline: "none" }}
        >
          {POLICY_UPDATE_TITLE}
        </Typography>

        <Typography variant="body1">
          Please read what has changed. To keep using Tradeloft, accept the new version.
        </Typography>

        <Box>
          <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
            What has changed
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, display: "grid", gap: 1 }}>
            {POLICY_UPDATE_CHANGES.map((change) => (
              <Typography key={change} component="li" variant="body2">
                {change}
              </Typography>
            ))}
          </Box>
        </Box>

        <Typography variant="body2">
          Read the full{" "}
          <Link component={RouterLink} to={ROUTES.PRIVACY} data-testid="policy-update-privacy-link">
            Privacy Policy
          </Link>{" "}
          and{" "}
          <Link component={RouterLink} to={ROUTES.TERMS} data-testid="policy-update-terms-link">
            Terms of Service
          </Link>
          .
        </Typography>

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={accept}
          disabled={saving}
          data-testid="policy-update-accept"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Accept and continue
        </Button>

        <Button variant="outlined" size="large" fullWidth onClick={signOut} data-testid="policy-update-sign-out">
          Sign out
        </Button>

        <Typography variant="caption" color="text.secondary">
          If you do not agree, you can sign out, or{" "}
          <Link component={RouterLink} to={ROUTES.DELETE_ACCOUNT_REQUEST} data-testid="policy-update-delete-link">
            ask us to delete your account
          </Link>
          .
        </Typography>
      </Box>
    </Dialog>
  );
};

PolicyUpdateGate.propTypes = {
  onAccepted: func.isRequired,
};

export default PolicyUpdateGate;
